import { buildApiUrl } from "./apiConfig";

const normalizeCars = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload?.cars && Array.isArray(payload.cars)) {
    return payload.cars;
  }
  return [];
};

const enhanceCar = (car = {}) => {
  const copy = { ...car };
  copy.driver_name =
    copy.driver_name || copy.account_holder || copy.user_id || "Unbekannt";
  copy.city = copy.city || copy.driver_city || copy.address_city || "—";
  copy.fuel_type = copy.fuel_type || null;
  copy.hasPhoto = Boolean(
    copy.has_verification_photo ?? copy.verification_photo_path
  );
  copy.photoEndpoint = copy.car_id
    ? buildApiUrl(`/admin/cars/${copy.car_id}/photo`)
    : null;
  copy.verification_photos = copy.verification_photos || {};
  copy.photoEndpoints = copy.car_id
    ? Object.fromEntries(
        Object.keys(copy.verification_photos).map((photoType) => [
          photoType,
          buildApiUrl(`/admin/cars/${copy.car_id}/photos/${photoType}`),
        ])
      )
    : {};
  return copy;
};

export async function fetchCars(fetchWithAuth) {
  if (typeof fetchWithAuth !== "function") {
    throw new Error("fetchWithAuth is required to load vehicles.");
  }

  try {
    const res = await fetchWithAuth(buildApiUrl("/admin/cars"));
    if (!res.ok) {
      throw new Error("Konnte Fahrzeuge nicht laden.");
    }
    const payload = await res.json().catch(() => ({}));
    const cars = normalizeCars(payload);
    return cars.map(enhanceCar);
  } catch (error) {
    throw error instanceof Error
      ? error
      : new Error("Unbekannter Fehler beim Laden der Fahrzeuge.");
  }
}

export async function updateCarStatus(fetchWithAuth, carId, nextStatus) {
  if (typeof fetchWithAuth !== "function") {
    throw new Error("fetchWithAuth is required to update vehicles.");
  }
  try {
    const res = await fetchWithAuth(buildApiUrl(`/admin/cars/${carId}/status`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: nextStatus }),
    });
    if (!res.ok) {
      throw new Error("Status konnte nicht aktualisiert werden.");
    }
    const payload = await res.json().catch(() => ({}));
    return payload?.car ? enhanceCar(payload.car) : null;
  } catch (error) {
    throw error instanceof Error
      ? error
      : new Error("Unbekannter Fehler beim Aktualisieren des Status.");
  }
}

export async function deleteCar(fetchWithAuth, carId) {
  if (typeof fetchWithAuth !== "function") {
    throw new Error("fetchWithAuth is required to delete vehicles.");
  }
  const res = await fetchWithAuth(buildApiUrl(`/admin/cars/${carId}`), {
    method: "DELETE",
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload?.detail || "Fahrzeug konnte nicht gelöscht werden.");
  }
  return res.json().catch(() => ({}));
}

export async function generateFleetLoginToken(fetchWithAuth, carId) {
  const res = await fetchWithAuth(buildApiUrl(`/admin/cars/${carId}/fleet-login-token`), { method: "POST" });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.detail || "Fleet-QR-Code konnte nicht erstellt werden.");
  return payload;
}

export async function fetchFleets(fetchWithAuth) {
  const res = await fetchWithAuth(buildApiUrl("/admin/fleets"));
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.detail || "Flotten konnten nicht geladen werden.");
  return payload?.fleets || [];
}

export async function fetchFleetCars(fetchWithAuth, fleetId) {
  const res = await fetchWithAuth(buildApiUrl(`/admin/fleets/${fleetId}/cars`));
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.detail || "Flottenfahrzeuge konnten nicht geladen werden.");
  return payload?.cars || [];
}

export async function createFleetCar(fetchWithAuth, fleetId, car) {
  const res = await fetchWithAuth(buildApiUrl(`/admin/fleets/${fleetId}/cars`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(car),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.detail || "Fahrzeug konnte nicht erstellt werden.");
  return payload;
}

export async function createFleet(fetchWithAuth, name, cars) {
  const res = await fetchWithAuth(buildApiUrl("/admin/fleets"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, cars }) });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.detail || "Flotte konnte nicht erstellt werden.");
  return payload;
}

export async function assignFleetCampaign(fetchWithAuth, fleetId, adId, carIds = []) {
  const res = await fetchWithAuth(buildApiUrl(`/admin/fleets/${fleetId}/campaign`), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ad_id: adId, car_ids: carIds }) });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.detail || "Kampagne konnte nicht zugewiesen werden.");
  return payload;
}
